import React, { useEffect, useLayoutEffect, useState, useRef, useMemo } from "react";
import CustomCalendar from "../../../components/CustomCalendar/CustomCalendar";
import CustomTimePicker from "../../../components/CustomTimePicker/CustomTimePicker";
import calendarIcon from "../../../common/images/Calendar.png";
import clockIcon from "../../../common/images/Clock.png";
import "./PatientHistory.scss";
import { connect } from "react-redux";
import {
    NX5TrendsFilters,
    NX5TrendsGraphFilters,
    NX5PatientHistoryDurationOptionList,
    NX5PatientHistoryIntervalOptionList,
    getNx5PatientHistoryIntervalsForDuration,
    VITAL_OPTION,
} from "../../../common/constant/common-constants";
import whiteUpArrow from "../../../common/icons/svg/white-up-arrow.svg";
import whiteDownArrow from "../../../common/icons/svg/white-down-arrow.svg";
import priority_high_svg from "../../../common/icons/svg/priority-high.svg";
import priority_medium_svg from "../../../common/icons/svg/priority-medium.svg";
import priority_low_svg from "../../../common/icons/svg/priority-low.svg";
import moment from "moment";
import ParameterGraph from "./ParameterGraph";
import TrendTable from "../../../components/Table/TrendTable/TrendTable";
import { nx5PatientHistoryTimeData, nx5PatientHistoryLiveAlarmHistoryData } from "../../../modules/actions/action";
import { getNX5PatientHistoryLiveAlarmHistoryData, getNX5PatientHistoryMonitorParameterHistoryData } from "../../../helper/patient-history-helper";
import { idToKey } from "../../../common/utils/NX5IdMap/MonitorParametersIdMap";
import lung_svg from "../../../common/icons/svg/lungs.svg";
import consolidation_svg from "../../../common/icons/svg/consolidation.svg";

const DEFAULT_DURATION_NAME = NX5PatientHistoryDurationOptionList[2]?.name ?? "60";
const DEFAULT_INTERVAL_NAME = "60";

const formatTrendDateDisplay = (date) => {
    if (!date) return "";
    return moment(date).format("DD - MM - YYYY");
};

const formatTrendTimeDisplay = (timeValue) => {
    if (!timeValue) return "";
    const parsed = moment(timeValue, ["HH:mm:ss", "HH:mm"], true);
    if (!parsed.isValid()) return "";
    return parsed.format("HH : mm : ss");
};

const toCustomTimePickerValue = (timeValue) => {
    if (!timeValue) return "12:00:00";
    const parsed = moment(timeValue, ["HH:mm:ss", "HH:mm"], true);
    return parsed.isValid() ? parsed.format("HH:mm:ss") : "12:00:00";
};

const NX5_KEY_TO_TABLE_LABEL = {
    hr: "HR",
    rr: "RR",
    spo2: "SPO₂",
    pr: "PR",
    temperature1: "TEMP1",
    temperature2: "TEMP2",
    systolic: "SYS",
    diastolic: "DIA",
    mean: "MEAN",
    etco2: "EtCO₂",
    fico2: "FiO₂",
    aw_rr: "AwRR",
    ibp1_systolic: "IBP1 SYS",
    ibp1_diastolic: "IBP1 DIA",
    ibp1_mean: "IBP1 MEAN",
    ibp2_systolic: "IBP2 SYS",
    ibp2_diastolic: "IBP2 DIA",
    ibp2_mean: "IBP2 MEAN",
};

const PRIORITY_STYLE = {
    HIGH: { icon: priority_high_svg, color: "#AD0606", textColor: "#fff" },
    MEDIUM: { icon: priority_medium_svg, color: "#FCC331", textColor: "#000" },
    LOW: { icon: priority_low_svg, color: "#3ADAE5", textColor: "#000" },
    DEFAULT: { icon: "", color: "#3ADAE5", textColor: "#000" },
};

function hasPatientHistoryTimeData(timeData) {
    return Boolean(timeData && typeof timeData === "object" && Object.keys(timeData).length > 0);
}

function nx5ParamKeyToLabel(key) {
    if (!key) return "";
    return NX5_KEY_TO_TABLE_LABEL[key] || String(key).toUpperCase();
}

function nx5ParamIdsToTableParameterList(parameterIds) {
    if (!Array.isArray(parameterIds)) return [];
    return parameterIds
        .map((paramId) => {
            const key = idToKey(paramId);
            if (!key) return null;
            return {
                id: paramId,
                name: key,
                label: nx5ParamKeyToLabel(key),
            };
        })
        .filter(Boolean);
}

const sortParametersById = (parameterList) => {
    return [...parameterList].sort((a, b) => parseInt(a.id, 10) - parseInt(b.id, 10));
};

const TABULAR_ALARM_TEXT_COLORS = {
    HIGH: "#AD0606",
    MEDIUM: "#FCC331",
    LOW: "#3ADAE5",
};

function nx5TabularPriorityFromEvent(event) {
    if (event == null) return null;
    if (typeof event === "string") {
        const u = event.toUpperCase();
        return u === "HIGH" || u === "MEDIUM" || u === "LOW" ? u : null;
    }
    if (typeof event === "object") {
        const p = event.priority ?? event.alarmPriority ?? event.Priority;
        if (typeof p === "string") return p.toUpperCase();
    }
    return null;
}

function nx5TabularTextColorForEvent(event) {
    const pr = nx5TabularPriorityFromEvent(event);
    return pr ? TABULAR_ALARM_TEXT_COLORS[pr] : undefined;
}

function buildTabularTrendFromNX5MonitorParameterHistoryDataAPI(
    valuesByParamId,
    parameterIds,
    intervalSecondsKey,
    timeData
) {
    const startMs = new Date(timeData?.startDateTime).getTime();
    const endMs = new Date(timeData?.endDateTime).getTime();
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
        return { data: { data: [] }, headerList: [] };
    }

    const parsedSec = parseInt(intervalSecondsKey, 10);
    const sec = Number.isNaN(parsedSec) ? 60 : parsedSec;
    const intervalMs = Math.max(1000, sec * 1000);

    const firstBucket = Math.floor(startMs / intervalMs) * intervalMs;
    const forwardHeaders = [];
    for (let b = firstBucket; b < endMs; b += intervalMs) {
        forwardHeaders.push(b);
    }
    if (forwardHeaders.length === 0) {
        forwardHeaders.push(firstBucket);
    }

    const headerList = forwardHeaders.slice().reverse();

    const bucketToRow = new Map();
    const ensureRow = (bucketMs) => {
        if (!bucketToRow.has(bucketMs)) {
            bucketToRow.set(bucketMs, { time: bucketMs });
        }
        return bucketToRow.get(bucketMs);
    };

    const valuesMap =
        valuesByParamId && typeof valuesByParamId === "object" && !Array.isArray(valuesByParamId)
            ? valuesByParamId
            : {};

    parameterIds.forEach((paramId) => {
        const nameKey = idToKey(paramId);
        if (!nameKey) return;

        const series = valuesMap[paramId] ?? valuesMap[String(paramId)] ?? [];
        if (!Array.isArray(series) || series.length === 0) return;

        const sorted = [...series].sort(
            (a, b) => new Date(a?.observed_at).getTime() - new Date(b?.observed_at).getTime()
        );

        sorted.forEach((point) => {
            if (!point || point.value == null || point.value === "") return;
            const tMs = new Date(point.observed_at).getTime();
            if (!Number.isFinite(tMs) || tMs < startMs || tMs >= endMs) return;

            const bucketMs = Math.floor(tMs / intervalMs) * intervalMs;
            const row = ensureRow(bucketMs);
            const textColor = nx5TabularTextColorForEvent(point.event);

            let display;
            if (typeof point.value === "number" && Number.isFinite(point.value)) {
                display = String(point.value);
            } else {
                const n = Number(point.value);
                display = Number.isFinite(n) ? String(n) : String(point.value);
            }

            row[nameKey] = textColor ? { value: display, textColor } : display;
        });
    });

    const dataRows = headerList.map((h) => {
        const existing = bucketToRow.get(h);
        if (existing) {
            return { ...existing, time: h };
        }
        return { time: h };
    });

    return {
        data: { data: dataRows },
        headerList,
    };
}

function nx5TabularTrendPayloadFromNX5MonitorParameterHistoryDataAPI(raw) {
    if (!raw || typeof raw !== "object") return {};
    if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
        return raw.data;
    }
    return raw;
}

/** Finest allowed bucket for this duration so one API response can be re-bucketed client-side for any valid interval. */
function nx5TabularApiFetchIntervalSeconds(durationKeyFromTimeData) {
    if (!durationKeyFromTimeData || durationKeyFromTimeData === "Custom") {
        return "30";
    }
    const allowed = getNx5PatientHistoryIntervalsForDuration(String(durationKeyFromTimeData));
    return allowed[0]?.name ?? "60";
}

function PatientHistory(props) {


    
    const [trendOption, setTrendOption] = useState(VITAL_OPTION.VITAL_GRAPH);
    const [showMoreAlarms, setShowMoreAlarms] = useState(false);
    const [isDurationOpen, setIsDurationOpen] = useState(false);
    const [isIntervalOpen, setIsIntervalOpen] = useState(false);
    const [isDurationCustom, setIsDurationCustom] = useState(null);
    const [duration, setDuration] = useState(null);
    const [interval, setInterval] = useState(DEFAULT_INTERVAL_NAME);
    const [isCustomPopupVisible, setIsCustomPopupVisible] = useState(false);
    const [activeTrendPicker, setActiveTrendPicker] = useState(null);
    const durationRef = useRef(null);
    const intervalRef = useRef(null);

    const [trendStartDate, setTrendStartDate] = useState(null);
    const [trendEndDate, setTrendEndDate] = useState(null);
    const [trendStartTime, setTrendStartTime] = useState(null);
    const [trendEndTime, setTrendEndTime] = useState(null);

    const [headerList, setHeaderList] = useState([]);

    const [tabularTimeColumnPx, setTabularTimeColumnPx] = useState(74);

    useLayoutEffect(() => {
        const rem = parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
        setTabularTimeColumnPx(4.6 * rem);
    }, []);

    const [data, setData] = useState({ data: [] });
    const [tabularValuesCache, setTabularValuesCache] = useState(null);
    const [monitorParameterHistoryLoading, setMonitorParameterHistoryLoading] = useState(false);

    let graphFilters = [
        { key: "all", label: "All", width: "1.98vw", parameterList: NX5TrendsGraphFilters.ALL },
    ];

    let tableFilters = [
        { key: "all", label: "All", width: "1.98vw", parameterList: NX5TrendsFilters.ALL },
    ];

    const [selectGraphTypeFilters, setSelectGraphTypeFilters] = useState(graphFilters[0]);
    const [selectTableTypeFilters, setSelectTableTypeFilters] = useState(tableFilters[0]);

    const [selectedParameterList, setSelectedParameterList] = useState([]);

    const [liveAlarmHistory, setLiveAlarmHistory] = useState(null);
    const [currentLiveAlarmPacket, setCurrentLiveAlarmPacket] = useState([]);

    const getPriorityStyle = (priority) => {
        const key = priority?.toUpperCase();
        return PRIORITY_STYLE[key] || PRIORITY_STYLE.DEFAULT;
    };

    const handleShowMoreAlarmsClick = (event) => {
        event.stopPropagation();
        setShowMoreAlarms(!showMoreAlarms);
    };

    const handleTrendShow = (option) => {
        setTrendOption(option);
    }

    const getTrendIntervals = (durationValue) => {
        const durationMinutes = parseInt(durationValue, 10);
        if (Number.isNaN(durationMinutes) || durationMinutes <= 0) {
            return null;
        }
        const now = new Date();

        const endDate = now;
        const startDate = new Date(
            now.getTime() - durationMinutes * 60 * 1000
        );

        const startTime = moment(startDate).format("HH:mm:ss");
        const endTime = moment(endDate).format("HH:mm:ss");

        return {
            startDate,
            startTime,
            endDate,
            endTime
        };
    };

    function combineToUTC(startDate, endDate, startTime, endTime) {
        const startDatetime = moment.utc(`${startDate} ${startTime}`, "YYYY-MM-DD HH:mm:ss");
        const endDatetime = moment.utc(`${endDate} ${endTime}`, "YYYY-MM-DD HH:mm:ss");
        const utcStartMoment = startDatetime.clone().subtract(5, "hours").subtract(30, "minutes");
        const utcStart = utcStartMoment.format("YYYY-MM-DD HH:mm:ss") + "+00";
        const utcEnd =
            endDatetime.clone().subtract(5, "hours").subtract(30, "minutes").format("YYYY-MM-DD HH:mm:ss") +
            "+00";
        return { utc_startDatetime: utcStart, utc_endDatetime: utcEnd };
    }

    const calculateTimestamps = (isCustomRange, duration) => {
        const timeData = {
            bedId: props?.bedId,
            duration: isCustomRange ? "Custom" : duration,
        };

        if (isCustomRange) {
            const finalTimeIntervals = combineToUTC(
                moment(trendStartDate).format("YYYY-MM-DD"),
                moment(trendEndDate).format("YYYY-MM-DD"),
                trendStartTime,
                trendEndTime
            );
            timeData.startDate = trendStartDate;
            timeData.startTime = trendStartTime;
            timeData.endDate = trendEndDate;
            timeData.endTime = trendEndTime;
            timeData.startDateTime = finalTimeIntervals?.utc_startDatetime;
            timeData.endDateTime = finalTimeIntervals?.utc_endDatetime;
        } else if (duration) {
            const trendIntervals = getTrendIntervals(duration);
            if (!trendIntervals) return;
            const finalTimeIntervals = combineToUTC(
                moment(trendIntervals.startDate).format("YYYY-MM-DD"),
                moment(trendIntervals.endDate).format("YYYY-MM-DD"),
                trendIntervals.startTime,
                trendIntervals.endTime
            );
            timeData.startDate = trendIntervals.startDate;
            timeData.startTime = trendIntervals.startTime;
            timeData.endDate = trendIntervals.endDate;
            timeData.endTime = trendIntervals.endTime;
            timeData.startDateTime = finalTimeIntervals?.utc_startDatetime;
            timeData.endDateTime = finalTimeIntervals?.utc_endDatetime;
        }

        props.nx5PatientHistoryTimeData(timeData);
    };

    const showCustomPopup = () => {
        setIsCustomPopupVisible(true);
    };

    const hideCustomPopupOnGenerate = () => {
        if (
            trendStartDate
            && trendEndDate
            && moment(trendEndDate).startOf("day").isBefore(moment(trendStartDate).startOf("day"))
        ) {
            return;
        }
        setIsCustomPopupVisible(false);
        calculateTimestamps(true, null);
    };

    const hideCustomPopupOnCancel = () => {
        setIsCustomPopupVisible(false);
    };

    function findRawLiveAlarmsPacket(current, list) {
        const start = current - 2000;
        const end = current + 2000;

        // --- binary search: first index >= start ---
        let left = 0, right = list.length - 1;
        let startIdx = list.length;

        while (left <= right) {
            let mid = (left + right) >> 1;
            let ts = new Date(list[mid].observedTime).getTime();

            if (ts >= start) {
                startIdx = mid;
                right = mid - 1;
            } else {
                left = mid + 1;
            }
        }

        // --- binary search: last index <= end ---
        left = 0;
        right = list.length - 1;
        let endIdx = -1;

        while (left <= right) {
            let mid = (left + right) >> 1;
            let ts = new Date(list[mid].observedTime).getTime();

            if (ts <= end) {
                endIdx = mid;
                left = mid + 1;
            } else {
                right = mid - 1;
            }
        }

        // return the range
        return startIdx <= endIdx ? list.slice(startIdx, endIdx + 1) : [];
    }


    useEffect(() => {
        if (!hasPatientHistoryTimeData(props.PatientHistoryTimeData)) {
            if (DEFAULT_DURATION_NAME) {
                calculateTimestamps(false, DEFAULT_DURATION_NAME);
            }
            return;
        }

        const timeInfo = props.PatientHistoryTimeData;
        if (timeInfo?.duration === "Custom") {
            setDuration(null);
            setIsDurationCustom(true);
        } else {
            const d = String(timeInfo?.duration ?? "");
            const valid = NX5PatientHistoryDurationOptionList.some((x) => x.name === d);
            if (d && !valid) {
                calculateTimestamps(false, DEFAULT_DURATION_NAME);
                return;
            }
            setDuration(valid ? d : DEFAULT_DURATION_NAME);
            setIsDurationCustom(false);
        }
        setTrendStartDate(timeInfo?.startDate);
        setTrendStartTime(timeInfo?.startTime);
        setTrendEndDate(timeInfo?.endDate);
        setTrendEndTime(timeInfo?.endTime);
    }, [props.PatientHistoryTimeData]);

    useEffect(() => {
        if (!hasPatientHistoryTimeData(props.PatientHistoryTimeData) || !props.bedId) {
            setLiveAlarmHistory(null);
            setCurrentLiveAlarmPacket([]);
            return;
        }

        let cancelled = false;

        const loadLiveAlarmHistoryData = async () => {
            const cached = props.PatientHistoryLiveAlarmHistoryData;
            if (
                cached &&
                Object.keys(cached).length > 0 &&
                cached.isLoading === false &&
                cached.liveAlarmHistory != null
            ) {
                if (!cancelled) {
                    setLiveAlarmHistory(cached.liveAlarmHistory);
                }
                return;
            }

            try {
                props.nx5PatientHistoryLiveAlarmHistoryData({
                    bedId: props.bedId,
                    isLoading: true,
                    liveAlarmHistory: {},
                });

                const timeData = props.PatientHistoryTimeData;
                const res = await getNX5PatientHistoryLiveAlarmHistoryData({
                    bedId: props.bedId,
                    startDateTime: timeData.startDateTime,
                    endDateTime: timeData.endDateTime,
                });

                if (cancelled) return;

                const payload = res?.data ?? res;
                setLiveAlarmHistory(payload);
                props.nx5PatientHistoryLiveAlarmHistoryData({
                    bedId: props.bedId,
                    isLoading: false,
                    liveAlarmHistory: payload ?? {},
                });
            } catch (err) {
                if (cancelled) return;
                setLiveAlarmHistory(null);
                props.nx5PatientHistoryLiveAlarmHistoryData({
                    bedId: props.bedId,
                    isLoading: false,
                    liveAlarmHistory: {},
                });
            }
        };

        (trendOption === VITAL_OPTION.VITAL_GRAPH) && loadLiveAlarmHistoryData();

        return () => {
            cancelled = true;
        };
    }, [props.PatientHistoryTimeData, props.bedId]);

    useEffect(() => {
        if (trendOption !== VITAL_OPTION.VITAL_TABLE) return;
        if (!hasPatientHistoryTimeData(props.PatientHistoryTimeData) || !props.bedId) return;

        const paramIds = selectTableTypeFilters.parameterList;
        if (!Array.isArray(paramIds) || paramIds.length === 0) {
            setTabularValuesCache(null);
            setData({ data: [] });
            setHeaderList([]);
            return;
        }

        let cancelled = false;
        setMonitorParameterHistoryLoading(true);
        setTabularValuesCache(null);
        setData({ data: [] });
        setHeaderList([]);

        const timeData = props.PatientHistoryTimeData;
        const apiInterval = nx5TabularApiFetchIntervalSeconds(timeData?.duration);

        getNX5PatientHistoryMonitorParameterHistoryData({
            bedId: props.bedId,
            startDateTime: timeData.startDateTime,
            endDateTime: timeData.endDateTime,
            interval: apiInterval,
            selectedParamList: paramIds,
        })
            .then((raw) => {
                if (cancelled) return;
                const payload = nx5TabularTrendPayloadFromNX5MonitorParameterHistoryDataAPI(raw);
                setTabularValuesCache(payload.values ?? {});
            })
            .catch(() => {
                if (cancelled) return;
                setTabularValuesCache(null);
                const fallback = buildTabularTrendFromNX5MonitorParameterHistoryDataAPI(
                    {},
                    paramIds,
                    interval,
                    timeData
                );
                setData(fallback.data);
                setHeaderList(fallback.headerList);
            })
            .finally(() => {
                if (!cancelled) setMonitorParameterHistoryLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [props.PatientHistoryTimeData, props.bedId, selectTableTypeFilters, trendOption]);

    useEffect(() => {
        if (trendOption !== VITAL_OPTION.VITAL_TABLE) return;
        if (!hasPatientHistoryTimeData(props.PatientHistoryTimeData)) return;
        const paramIds = selectTableTypeFilters.parameterList;
        if (!Array.isArray(paramIds) || paramIds.length === 0) return;
        if (!tabularValuesCache) return;

        const timeData = props.PatientHistoryTimeData;
        const { data: nextData, headerList: nextHeaders } = buildTabularTrendFromNX5MonitorParameterHistoryDataAPI(
            tabularValuesCache,
            paramIds,
            interval,
            timeData
        );
        setData(nextData);
        setHeaderList(nextHeaders);
    }, [trendOption, tabularValuesCache, interval, props.PatientHistoryTimeData, selectTableTypeFilters]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (durationRef.current && !durationRef.current.contains(event.target)) {
                setIsDurationOpen(false);
            }
            if (intervalRef.current && !intervalRef.current.contains(event.target)) {
                setIsIntervalOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);

        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (!isCustomPopupVisible) {
            setActiveTrendPicker(null);
        }
    }, [isCustomPopupVisible]);

    useEffect(() => {
        if (!isCustomPopupVisible || !activeTrendPicker) return undefined;

        const handleTrendPickerClickOutside = (event) => {
            if (!event.target.closest(".custom-trends-field")) {
                setActiveTrendPicker(null);
            }
        };

        document.addEventListener("mousedown", handleTrendPickerClickOutside);
        return () => document.removeEventListener("mousedown", handleTrendPickerClickOutside);
    }, [isCustomPopupVisible, activeTrendPicker]);

    const toggleTrendPicker = (pickerId) => {
        setActiveTrendPicker((current) => (current === pickerId ? null : pickerId));
    };

    const handleTrendStartDateChange = (date) => {
        setTrendStartDate(date);
        setActiveTrendPicker(null);
    };

    const handleTrendEndDateChange = (date) => {
        setTrendEndDate(date);
        setActiveTrendPicker(null);
    };

    useEffect(() => {
        if (!liveAlarmHistory || !props?.PatientHistoryCurrentCursorTime?.currentCursorTime) return;

        const current = new Date(props.PatientHistoryCurrentCursorTime.currentCursorTime).getTime();
        const cursorKey = props.PatientHistoryCurrentCursorTime.currentCursorTime;

        if (liveAlarmHistory.dataType === "raw") {
            const alarms = liveAlarmHistory.alarms;
            if (!Array.isArray(alarms) || alarms.length === 0) return;
            setCurrentLiveAlarmPacket(findRawLiveAlarmsPacket(current, alarms));
            return;
        }

        if (liveAlarmHistory.dataType === "scattered") {
            const alarms = liveAlarmHistory.alarms;
            if (!alarms || typeof alarms !== "object" || Object.keys(alarms).length === 0) return;
            const packet = alarms[cursorKey];
            setCurrentLiveAlarmPacket(
                Array.isArray(packet) && packet.length > 0 ? packet : []
            );
        }
    }, [liveAlarmHistory, props.PatientHistoryCurrentCursorTime]);

    const isTrendEndBeforeStart = Boolean(
        trendStartDate
        && trendEndDate
        && moment(trendEndDate).startOf("day").isBefore(moment(trendStartDate).startOf("day"))
    );

    const alarmsList = Array.isArray(currentLiveAlarmPacket) ? currentLiveAlarmPacket : [];
    const hasAlarms = alarmsList.length > 0;
    const firstAlarm = hasAlarms ? alarmsList[0] : null;

    const tableParameterList = useMemo(
        () =>
            sortParametersById(
                nx5ParamIdsToTableParameterList(selectTableTypeFilters.parameterList).filter(
                    (param) => param.label !== "All"
                )
            ),
        [selectTableTypeFilters]
    );

    const durationOptionsForUi = NX5PatientHistoryDurationOptionList;

    const intervalOptionsForUi = useMemo(() => {
        if (isDurationCustom || !duration) return NX5PatientHistoryIntervalOptionList;
        return getNx5PatientHistoryIntervalsForDuration(duration);
    }, [isDurationCustom, duration]);

    useEffect(() => {
        if (trendOption !== VITAL_OPTION.VITAL_TABLE) return;
        if (!duration || isDurationCustom) return;
        const allowed = getNx5PatientHistoryIntervalsForDuration(duration);
        if (!allowed.length) return;
        if (!allowed.some((o) => o.name === interval)) {
            setInterval(allowed[0].name);
        }
    }, [trendOption, duration, isDurationCustom, interval]);

    return (
        <div className="history-container">
            <div className="history-container-left">
                <div className="patient-history-details">
                    <div className="left">
                        <div className="trend-heading">Review</div>
                        <div className='device-select-btn-container'>
                            <div
                                className={`device-btn ${trendOption === VITAL_OPTION.VITAL_GRAPH ? "active-device-component" : ""}`}
                                onClick={() => handleTrendShow(VITAL_OPTION.VITAL_GRAPH)}                        >
                                Graphical
                            </div>
                            <div
                                className={`device-btn ${trendOption === VITAL_OPTION.VITAL_TABLE ? "active-device-component" : ""}`}
                                onClick={() => handleTrendShow(VITAL_OPTION.VITAL_TABLE)}
                            >
                                Tabular
                            </div>
                        </div>
                    </div>

                    {props.monitorConnected && props.monitorConnected.isDemo && (
                        <div className="patient-history-demo-note">
                            No Trends will sync during Demo Mode
                        </div>
                    )}

                    <div className="right">
                        <div className="dd-input" ref={durationRef}>
                            <div
                                className="dropdown-container"
                                onClick={() => {
                                    setIsDurationOpen((open) => !open);
                                    setIsIntervalOpen(false);
                                }}
                            >
                                <div
                                    className="dropdown-trigger"
                                >
                                    <span className="dropdown-trigger-label">
                                        {isDurationCustom
                                            ? "Custom"
                                            : NX5PatientHistoryDurationOptionList.find(
                                                (option) => option.name === duration
                                            )?.label}
                                    </span>
                                    <img
                                        className="dropdown-arrow"
                                        src={isDurationOpen ? whiteUpArrow : whiteDownArrow}
                                        alt="▼"
                                    />
                                </div>

                                {isDurationOpen && (
                                    <div className="dropdown-menu">
                                        {durationOptionsForUi.map((option) => (
                                            <div
                                                key={option.name}
                                                className={`dropdown-item ${duration === option.name ? "selected" : ""
                                                    }`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setIsDurationOpen(false);
                                                    calculateTimestamps(false, option.name)

                                                }}
                                            >
                                                {option.label}
                                            </div>
                                        ))}
                                        <div
                                            className="dropdown-custom-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                showCustomPopup();
                                                setIsDurationOpen(false);
                                            }}
                                        >
                                            Custom
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {VITAL_OPTION.VITAL_TABLE === trendOption && (
                            <div className="dd-input" ref={intervalRef}>
                                <div
                                    className="dropdown-container"
                                    onClick={() => {
                                        setIsIntervalOpen((open) => !open);
                                        setIsDurationOpen(false);
                                    }}
                                >
                                    <div className="dropdown-trigger">
                                        <span className="dropdown-trigger-label">
                                            {NX5PatientHistoryIntervalOptionList.find((option) => option.name === interval)
                                                ?.label ?? NX5PatientHistoryIntervalOptionList[0]?.label}
                                        </span>
                                        <img
                                            className="dropdown-arrow"
                                            src={isIntervalOpen ? whiteUpArrow : whiteDownArrow}
                                            alt="▼"
                                        />
                                    </div>

                                    {isIntervalOpen && (
                                        <div className="dropdown-menu">
                                            {intervalOptionsForUi.map((option) => (
                                                <div
                                                    key={option.name}
                                                    className={`dropdown-item ${interval === option.name ? "selected" : ""
                                                        }`}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setIsIntervalOpen(false);
                                                        setInterval(option.name);
                                                    }}
                                                >
                                                    {option.label}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                    </div>
                </div>

                {VITAL_OPTION.VITAL_GRAPH === trendOption && (
                    <>
                        <div className="alarms-container">
                            <div
                                className="alarm-container"
                                onClick={handleShowMoreAlarmsClick}
                                id="alarm-display-bar"
                            >
                                {hasAlarms ? (
                                    <>
                                        <div className="top-alarm-container">
                                            {(firstAlarm?.type === "PHYSIOLOGICAL" || firstAlarm?.type === "TECHNICAL") &&
                                                getPriorityStyle(firstAlarm.priority)?.icon && (
                                                    <img
                                                        alt="priority icon"
                                                        src={getPriorityStyle(firstAlarm.priority)?.icon || ""}
                                                        className="priority"
                                                    />
                                                )}
                                            {firstAlarm?.type === "OPERATIONAL" && (
                                                <img
                                                    alt="priority icon"
                                                    src={firstAlarm?.category === "additional_feature" ? lung_svg : consolidation_svg}
                                                    className="priority"
                                                />
                                            )}
                                            {firstAlarm?.message || ""}
                                        </div>

                                        <div className="alarm-number-container">
                                            {alarmsList.length}
                                            {showMoreAlarms ? (
                                                <img src={whiteUpArrow} className="arrow-icon" alt="up arrow" />
                                            ) : (
                                                <img src={whiteDownArrow} className="arrow-icon" alt="down arrow" />
                                            )}
                                        </div>

                                        {showMoreAlarms && (
                                            <div className="more-alarm-container">
                                                {alarmsList.map((alarm, index) => (
                                                    <div className="alarm more-alarm" key={index}>

                                                        {(alarm?.type === "PHYSIOLOGICAL" || alarm?.type === "TECHNICAL") &&
                                                            getPriorityStyle(alarm?.priority)?.icon && (
                                                                <img
                                                                    alt="priority icon"
                                                                    src={getPriorityStyle(alarm?.priority)?.icon || ""}
                                                                    className="priority"
                                                                />
                                                            )
                                                        }
                                                        {(alarm?.type === "OPERATIONAL") &&
                                                            <img
                                                                alt="priority icon"
                                                                src={alarm?.category === "additional_feature" ? lung_svg : consolidation_svg}
                                                                className="priority"
                                                            />
                                                        }
                                                        {alarm?.message || ""}
                                                        <span className="alarm-time">
                                                            {alarm?.observedTime
                                                                ? moment(alarm.observedTime).utcOffset(330).format("HH:mm:ss")
                                                                : ""}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="no-alarm-container">
                                        <span>No active alarms</span>
                                    </div>
                                )}
                            </div>
                        </div>


                        {/* <div className="patient-history-graph-types">
                            {graphFilters.map((type) => (
                                <div
                                    key={type.key}
                                    className={`graph-type ${selectGraphTypeFilters.key === type.key ? "selected" : ""}`}
                                    style={{ width: type.width }}
                                    onClick={() => setSelectGraphTypeFilters(type)}
                                >
                                    {type.label}
                                </div>
                            ))}
                        </div> */}

                        <ParameterGraph
                            selectedParamList={selectGraphTypeFilters.parameterList}
                            bedId={props.bedId}
                        />
                    </>
                )}

                {VITAL_OPTION.VITAL_TABLE === trendOption && (
                    <div className="trend-table-container">
                        {monitorParameterHistoryLoading ? (
                            <div className="spinner-container">
                                <div className="spinner" />
                            </div>
                        ) : tableParameterList.length > 0 && headerList.length > 0 ? (
                            <div className="nx5-trend-chart-scroll-wrap">
                                <TrendTable
                                    headerList={headerList}
                                    parameterList={tableParameterList}
                                    data={data}
                                    virtualTimeColumnWidthPx={tabularTimeColumnPx}
                                />
                            </div>
                        ) : tableParameterList.length > 0 ? (
                            <div className="patient-history-tabular-loading">
                                No tabular columns for this duration and interval.
                            </div>
                        ) : null}
                    </div>
                )}
            </div>
            {isCustomPopupVisible && (
                <div className="fullscreen-overlay">
                    <div className="overlay-content custom-trends-popup">
                        <div className="top">
                            <div className="top-heading">Custom Trends</div>
                        </div>
                        <div className="middle custom-trends-form">
                            <div className="custom-trends-field start-date">
                                <span className="custom-trends-field-label">Start Date</span>
                                <button
                                    type="button"
                                    className="custom-trends-field-trigger"
                                    onClick={() => toggleTrendPicker("startDate")}
                                >
                                    <span className="custom-trends-field-value">
                                        {formatTrendDateDisplay(trendStartDate)}
                                    </span>
                                    <img src={calendarIcon} alt="" aria-hidden="true" />
                                </button>
                                {activeTrendPicker === "startDate" && (
                                    <div className="custom-trends-picker-popover">
                                        <CustomCalendar
                                            value={trendStartDate}
                                            onChange={handleTrendStartDateChange}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="custom-trends-field start-time">
                                <span className="custom-trends-field-label">Start Time</span>
                                <button
                                    type="button"
                                    className="custom-trends-field-trigger"
                                    onClick={() => toggleTrendPicker("startTime")}
                                >
                                    <span className="custom-trends-field-value">
                                        {formatTrendTimeDisplay(trendStartTime)}
                                    </span>
                                    <img src={clockIcon} alt="" aria-hidden="true" />
                                </button>
                                {activeTrendPicker === "startTime" && (
                                    <div className="custom-trends-picker-popover custom-trends-picker-popover--time">
                                        <CustomTimePicker
                                            includeSeconds
                                            value={toCustomTimePickerValue(trendStartTime)}
                                            onChange={setTrendStartTime}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="custom-trends-field end-date">
                                <span className="custom-trends-field-label">End Date</span>
                                <button
                                    type="button"
                                    className="custom-trends-field-trigger"
                                    onClick={() => toggleTrendPicker("endDate")}
                                >
                                    <span className="custom-trends-field-value">
                                        {formatTrendDateDisplay(trendEndDate)}
                                    </span>
                                    <img src={calendarIcon} alt="" aria-hidden="true" />
                                </button>
                                {activeTrendPicker === "endDate" && (
                                    <div className="custom-trends-picker-popover">
                                        <CustomCalendar
                                            value={trendEndDate}
                                            onChange={handleTrendEndDateChange}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="custom-trends-field end-time">
                                <span className="custom-trends-field-label">End Time</span>
                                <button
                                    type="button"
                                    className="custom-trends-field-trigger"
                                    onClick={() => toggleTrendPicker("endTime")}
                                >
                                    <span className="custom-trends-field-value">
                                        {formatTrendTimeDisplay(trendEndTime)}
                                    </span>
                                    <img src={clockIcon} alt="" aria-hidden="true" />
                                </button>
                                {activeTrendPicker === "endTime" && (
                                    <div className="custom-trends-picker-popover custom-trends-picker-popover--time">
                                        <CustomTimePicker
                                            includeSeconds
                                            value={toCustomTimePickerValue(trendEndTime)}
                                            onChange={setTrendEndTime}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        {isTrendEndBeforeStart && (
                            <div className="custom-trends-date-error">
                                Start date must be smaller than end date
                            </div>
                        )}
                        <div className="bottom">
                            <button className="cancel-trends" onClick={hideCustomPopupOnCancel}>Cancel</button>
                            <button
                                type="button"
                                className="generate-trends"
                                disabled={isTrendEndBeforeStart}
                                onClick={hideCustomPopupOnGenerate}
                            >
                                Generate Trends
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

const mapStateToProps = (state, ownProps) => ({
    PatientHistoryTimeData: state.volatileDataReducer?.nx5PatientHistoryTimeData?.[ownProps.bedId],
    PatientHistoryCurrentCursorTime:
        state.volatileDataReducer?.nx5PatientHistoryCurrentCursorTime?.[ownProps.bedId],
    PatientHistoryLiveAlarmHistoryData:
        state.volatileDataReducer?.nx5PatientHistoryLiveAlarmHistoryData?.[ownProps.bedId],
});

const mapDispatchToProps = (dispatch) => ({
    nx5PatientHistoryTimeData: (payload) => dispatch(nx5PatientHistoryTimeData(payload)),
    nx5PatientHistoryLiveAlarmHistoryData: (payload) => dispatch(nx5PatientHistoryLiveAlarmHistoryData(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PatientHistory);